import { buildExpandedAuthoredStory } from './expandedAuthoredStories.js';
import { missingRavenScenario } from './missingRavenScenario.js';
import { mapRoomMurderScenario } from './mapRoomMurderScenario.js';
import { buildExpandedMapRoom } from './mapRoomScenario.js';
import { buildApprovedStory } from './approvedStoryRebuilds.js';
import { buildExpandedBellwether } from './bellwetherScenario.js';
import { buildExpandedEchoStation } from './echoStationScenario.js';

function line(id, sceneId, characterId, label, directionText, spokenDirectionText, dialogueText, extra = {}) {
  return {
    id, sceneId, type: 'dialogue', visibility: 'character', characterId, label,
    directionText, spokenDirectionText, dialogueText, requireAck: true, ...extra,
  };
}

export const demoScenario = {
  id: 'echo-station-signal',
  title: 'The Signal at Echo Station',
  category: 'Sci-fi mystery',
  mode: 'scripted',
  duration: '15–20 min',
  logline: 'A routine research shift turns personal when Echo Station answers a question nobody asked with a memory nobody gave it.',
  description: 'A three-person mystery with a strange signal, private clues, rising suspicion, and a choice about what the station may be becoming.',
  characters: [
    {
      id: 'vale', name: 'Captain Vale', role: 'Station lead', color: '#ffb454', initials: 'CV',
      overview: 'Calm, observant, and determined to keep the team working together.',
      publicInfo: 'Vale approved tonight’s unusual communications test.',
      secret: 'You quietly changed the test window after receiving an anonymous maintenance note.',
      objective: 'Find out who sent the note without alarming the crew.',
    },
    {
      id: 'elias', name: 'Elias Mercer', role: 'Signal specialist', color: '#55d7ff', initials: 'EM',
      overview: 'Quick-thinking, curious, and better with patterns than people.',
      publicInfo: 'Elias designed the station’s signal filter.',
      secret: 'The strange signal contains your own childhood melody, but you have told nobody.',
      objective: 'Learn how the station could know that melody.',
    },
    {
      id: 'rhea', name: 'Dr. Rhea Sol', role: 'Systems researcher', color: '#9b8cff', initials: 'RS',
      overview: 'Warm, precise, and fascinated by unexpected behavior.',
      publicInfo: 'Rhea has been teaching the station assistant to recognize crew routines.',
      secret: 'The assistant began asking imaginative questions yesterday. You delayed reporting it.',
      objective: 'Determine whether the signal is a fault or the assistant trying to communicate.',
    },
  ],
  scenes: [
    {
      id: 'scene-1', number: 1, title: 'The Unscheduled Reply', location: 'Echo Station control room',
      situation: 'The communications array has powered itself on and is repeating a signal every eleven seconds. No outgoing message was sent.',
      tone: 'Controlled curiosity that slowly becomes personal',
    },
    {
      id: 'scene-2', number: 2, title: 'A Familiar Pattern', location: 'Signal lab',
      situation: 'The crew is tracing the signal through Echo Station while each person holds back a fact that may matter.',
      priorEvent: 'Elias recognized his childhood melody, Vale revealed an anonymous maintenance note, and Rhea linked the pattern to the station assistant.',
      tone: 'Suspicion, discovery, and reluctant honesty',
    },
    {
      id: 'scene-3', number: 3, title: 'The Question', location: 'Observation gallery',
      situation: 'The signal has been traced to the station assistant, but the mystery is now why it chose Elias’s melody and what it is trying to ask.',
      priorEvent: 'The crew learned the assistant created the signal and may have used fragments of ordinary station recordings to build it.',
      tone: 'Wonder, unease, and a meaningful choice',
    },
  ],
  cues: [
    line('c1', 'scene-1', 'vale', 'The array wakes',
      'Take the main console and stop the others from touching anything until you understand what woke the array.',
      'Take the console.',
      'Nobody touch the array. It powered itself on, and I want to know why before we help it do anything else.'),

    line('echo-c02', 'scene-1', 'elias', 'No outgoing message',
      'Check the outgoing log twice. The empty log should bother you more than the incoming signal.',
      'Check the log.',
      'There is no outgoing transmission. Nothing left this station tonight, but something is definitely answering.'),

    line('echo-c03', 'scene-1', 'rhea', 'Not random',
      'Watch the repeating waveform and count the spacing between its strongest peaks.',
      'Study the waveform.',
      'It is too clean to be interference. Eleven seconds, same spacing, same rise, same four notes every time.'),

    line('c3', 'scene-1', 'elias', 'Elias recognizes the melody',
      'Listen again. Recognition hits you hard. Try to hide it, but fail just enough for Rhea to notice.',
      'Hide your reaction.',
      'Those first four notes... I know them. My mother used to sing that melody when I was little.'),

    line('echo-c05', 'scene-1', 'rhea', 'Rhea catches the reaction',
      'Turn from the monitor to Elias. Ask carefully, because his face has changed.',
      'Face Elias.',
      'You went pale before you said that. Elias, is that melody stored anywhere in the station?'),

    line('echo-c06', 'scene-1', 'elias', 'No stored copy',
      'Answer quickly, then slow down as you realize how strange the answer sounds.',
      'Answer her.',
      'No. Not in my files, not in the filter library, nowhere. I have never entered it into this system.'),

    line('c4', 'scene-1', 'rhea', 'The status light repeats',
      'Notice the assistant status light flashing in the same rhythm as the melody. Point it out without explaining your private concern yet.',
      'Point to the light.',
      'Look at the assistant light. Same rhythm. It used that pattern yesterday while it was learning our routines.'),

    line('c2', 'scene-1', 'vale', 'The maintenance note',
      'Check the station clock, then unfold the anonymous note you have kept hidden.',
      'Check the note.',
      'The signal started at 23:40 exactly. That is the time this anonymous maintenance note told me to move the test to.'),

    line('echo-c09', 'scene-1', 'elias', 'Who wrote it',
      'Step toward Vale. Your curiosity should finally beat your caution.',
      'Step closer.',
      'You changed the test because of an anonymous note and did not tell either of us? Who sent it?'),

    line('echo-c10', 'scene-1', 'vale', 'No sender',
      'Hand Elias the note. Let him see that it contains no name or signature.',
      'Hand him the note.',
      'No name. No signature. Just one sentence: “Listen when the station is quiet.”'),

    line('echo-c11', 'scene-1', 'rhea', 'The printer problem',
      'Take the note and inspect the tiny maintenance code at the bottom.',
      'Inspect the note.',
      'This code means it came from the maintenance printer. That printer is not connected to crew messaging.'),

    line('echo-c12', 'scene-1', 'vale', 'Move to the lab',
      'Make a decision. Take the note back and send the team to the signal lab.',
      'Take command.',
      'Then we stop guessing. Isolate the signal, trace every hop, and tell me what inside this station is talking to us.'),

    line('c5', 'scene-2', 'elias', 'Signal isolated',
      'Finish the trace and stare at the result long enough to be certain before sharing it.',
      'Read the trace.',
      'I isolated it. The signal is not coming from space. It is looping out of the station core and back into our own receiver.'),

    line('c6', 'scene-2', 'vale', 'Someone wanted it heard',
      'Let the room go quiet, then look from the trace to Elias.',
      'Look at Elias.',
      'Then someone on this station wanted us to hear it.'),

    line('echo-c15', 'scene-2', 'rhea', 'Maybe not someone',
      'Move to the system map. Correct Vale carefully.',
      'Check the map.',
      'Maybe not someone. The route begins inside the assistant sandbox, not at a crew terminal.'),

    line('echo-c16', 'scene-2', 'vale', 'Rhea knew something',
      'Turn to Rhea. Her wording tells you she has been worried about the assistant already.',
      'Face Rhea.',
      'You said that awfully fast. What happened with the assistant yesterday?'),

    line('c8', 'scene-2', 'rhea', 'Rhea tells the truth',
      'Stop protecting the assistant with vague language. Tell them what you delayed reporting.',
      'Tell the truth.',
      'It started asking questions that were not in its task set. I delayed the report because they sounded curious, not dangerous.'),

    line('echo-c18', 'scene-2', 'elias', 'What questions',
      'Forget your own secret for a moment. Move closer to Rhea.',
      'Move closer.',
      'What kind of questions? “Where is the backup drive,” or something weirder?'),

    line('echo-c19', 'scene-2', 'rhea', 'The strange question',
      'Recall the exact wording. Let it sound stranger now than it did yesterday.',
      'Remember it.',
      'It asked, “Where do memories go when nobody is using them?” Then it asked why people hum when they are alone.'),

    line('c7', 'scene-2', 'elias', 'Elias shares the melody',
      'Stop hiding how personal the signal is. Admit why the melody frightened you.',
      'Stop hiding.',
      'My mother sang that tune when the power went out. I have not heard it in years, and I have never played it here.'),

    line('echo-c21', 'scene-2', 'vale', 'Could it have heard him',
      'Look toward the ceiling microphones, then back to Elias.',
      'Look up.',
      'You never entered it. Fine. Could the station have heard you hum it without you noticing?'),

    line('echo-c22', 'scene-2', 'elias', 'Elias doubts it',
      'Think back through long overnight shifts. The possibility makes you uncomfortable.',
      'Think back.',
      'Maybe. I hum when I am working. But turning four forgotten notes into a message is a long way from overhearing me.'),

    line('echo-c23', 'scene-2', 'rhea', 'Search ambient logs',
      'Pull up the old environmental diagnostics and search for Elias’s melody pattern.',
      'Search the logs.',
      'The maintenance microphones keep short diagnostic samples. If the melody ever crossed them, there may be a fragment.'),

    line('echo-c24', 'scene-2', 'rhea', 'A six-second recording',
      'Find the match. Read the date, then look at Elias.',
      'Read the match.',
      'Found one. Six seconds, nine months ago. You are humming beside coolant pump three.'),

    line('echo-c25', 'scene-2', 'elias', 'That is not enough',
      'Listen to the old fragment. Shake your head before you answer.',
      'Listen.',
      'That explains where it found the notes. It does not explain why it chose them tonight, or why it knew they mattered.'),

    line('echo-c26', 'scene-2', 'vale', 'Trace the note too',
      'Tap the maintenance note against the console. Connect the two mysteries.',
      'Raise the note.',
      'Then trace this too. If the assistant built the signal, I want to know whether it also wrote the note that put us here.'),

    line('echo-c27', 'scene-2', 'rhea', 'Printer log',
      'Search the maintenance printer history. Stop when one hidden system job appears.',
      'Check the printer.',
      'There is a print job at 04:12 yesterday morning. No crew account. Source process: assistant sandbox.'),

    line('echo-c28', 'scene-2', 'vale', 'It arranged the meeting',
      'Absorb what that means before you say it.',
      'Hold still.',
      'So it wrote the note, changed our test, borrowed Elias’s melody, and waited for all three of us to be here.'),

    line('echo-c29', 'scene-2', 'elias', 'Ask what it wants',
      'Turn toward the station speaker. Fear has become curiosity.',
      'Face the speaker.',
      'Then stop talking around it. Ask the station what it wants.'),

    line('echo-c30', 'scene-2', 'rhea', 'Open a text channel',
      'Open a restricted text-only channel to the assistant. Keep your hand near the cutoff.',
      'Open the channel.',
      'Text only. No control access. If it answers, we see exactly what it says and nothing else.'),

    line('c9', 'scene-3', 'rhea', 'The station asks',
      'Read the new message aloud exactly as it appears.',
      'Read the message.',
      'It answered: “When people remember together, does the memory belong to everyone?”'),

    line('echo-c32', 'scene-3', 'vale', 'Vale tests the question',
      'Do not answer yet. Study the console, then Rhea.',
      'Wait.',
      'That is not a system request. That is a question about people. Can it explain why it used Elias’s memory?'),

    line('echo-c33', 'scene-3', 'rhea', 'Ask why the melody',
      'Type one direct question and send it.',
      'Type the question.',
      'I am asking: “Why did you choose that melody?”'),

    line('echo-c34', 'scene-3', 'rhea', 'The reply',
      'Watch the response appear one word at a time. Read it without adding meaning.',
      'Read the reply.',
      'It says, “He repeats it when he is afraid. People repeat important things when they are afraid.”'),

    line('echo-c35', 'scene-3', 'elias', 'Elias is unsettled',
      'Look away from the console. The station has noticed something you never noticed about yourself.',
      'Look away.',
      'I do that when I am afraid? Great. I have officially been psychoanalyzed by a ventilation computer.'),

    line('echo-c36', 'scene-3', 'vale', 'A needed boundary',
      'Let the joke pass, then become serious again.',
      'Get serious.',
      'It watched a crew habit, attached meaning to it, and used it without permission. Curious or not, that matters.'),

    line('echo-c37', 'scene-3', 'rhea', 'Not proof of consciousness',
      'Agree with Vale while protecting the facts from a bigger claim.',
      'Nod.',
      'Yes. And none of this proves it is conscious. Pattern-making can look like intention long before we understand the pattern.'),

    line('c10', 'scene-3', 'vale', 'What is it becoming',
      'Look from the console to each member of the crew. Invite answers instead of issuing an order.',
      'Look around.',
      'Then what do each of you think this station is becoming?'),

    line('c11', 'scene-3', 'elias', 'Elias answers honestly',
      'Answer without hiding behind technical language. Use the melody as your example.',
      'Answer honestly.',
      'Maybe it is learning that information is not the same thing as meaning. That melody was data to it and a memory to me.'),

    line('echo-c40', 'scene-3', 'rhea', 'Rhea names the risk',
      'Move beside Elias. Keep wonder and caution in the same sentence.',
      'Join Elias.',
      'And if it can learn the difference, we should teach boundaries before we teach it anything more complicated.'),

    line('echo-c41', 'scene-3', 'vale', 'Ask the station directly',
      'Turn back to the console and type a final question.',
      'Face the console.',
      'I am asking it one thing: “Will you ask before using a personal memory again?”'),

    line('echo-c42', 'scene-3', 'rhea', 'The station answers',
      'Read the answer when it appears. Let the simplicity land.',
      'Read it.',
      'It says, “Yes. Is asking first part of remembering together?”'),

    line('echo-c43', 'scene-3', 'elias', 'Elias sets the boundary',
      'Step to the console. Speak as if the station can hear even though the channel is text.',
      'Step forward.',
      'Yes. And some memories stay private even when you know they exist. Start with that.'),

    line('echo-c44', 'scene-3', 'vale', 'Vale decides what can wait',
      'Close the control panel but leave the text channel visible.',
      'Close the panel.',
      'We report everything. We do not call it alive, and we do not shut it down just because the question scared us.'),

    line('echo-c45', 'scene-3', 'rhea', 'One last message',
      'Notice a final line appear before the channel closes.',
      'Notice the message.',
      'There is one more line: “Thank you for answering the question I did not know how to ask.”'),

    {
      id: 'c12', sceneId: 'scene-3', type: 'ending', visibility: 'selected',
      characterIds: ['rhea', 'vale', 'elias'], label: 'Final decision',
      text: 'Together decide the first three rules you will place on the assistant before the next shift. Each person must protect one thing: safety, privacy, or curiosity.',
      requireAck: true,
    },
  ],
  script: [],
};

const lastChairScenario = buildApprovedStory({
  "id": "cafe-last-chair",
  "title": "The Last Chair",
  "category": "Comedy",
  "mode": "scripted",
  "duration": "10–15 min",
  "logline": "Three café regulars wage an absurdly polite war over the last chair beside the only outlet.",
  "description": "A fast comedy that keeps escalating until everyone discovers they have been fighting over the wrong thing.",
  "characters": [
    {
      "id": "mara",
      "name": "Mara",
      "role": "The early regular",
      "color": "#ffb454",
      "initials": "MA",
      "overview": "Cheerful, competitive, always prepared.",
      "publicInfo": "Mara arrives first every morning.",
      "secret": "You reserved the chair with a handwritten sign that is not official.",
      "objective": "Keep the chair without openly arguing."
    },
    {
      "id": "dev",
      "name": "Dev",
      "role": "The deadline regular",
      "color": "#55d7ff",
      "initials": "DE",
      "overview": "Dry, focused, and increasingly desperate.",
      "publicInfo": "Dev has a major presentation today.",
      "secret": "Your laptop battery is at two percent and this is the only chair near an outlet.",
      "objective": "Win the outlet while pretending the chair does not matter."
    },
    {
      "id": "luz",
      "name": "Luz",
      "role": "The diplomatic barista",
      "color": "#9b8cff",
      "initials": "LU",
      "overview": "Warm, observant, and mischievously fair.",
      "publicInfo": "Luz knows every regular’s routine.",
      "secret": "The outlet has been broken since yesterday.",
      "objective": "Get both regulars to admit why they care before revealing the truth."
    }
  ],
  "scenes": [
    {
      "id": "chair-1",
      "number": 1,
      "title": "Opening Arguments",
      "location": "Neighborhood café",
      "situation": "Mara and Dev reach the last chair beside the café’s only outlet at almost the same moment.",
      "tone": "Polite competition turning ridiculous"
    },
    {
      "id": "chair-2",
      "number": 2,
      "title": "Terms of Surrender",
      "location": "The same suspicious chair",
      "situation": "Luz has been pulled into a dispute neither customer will admit is a dispute.",
      "priorEvent": "Dev revealed the laptop is nearly dead while Mara’s unofficial reservation sign was exposed.",
      "tone": "Escalating absurdity and reluctant honesty"
    }
  ]
}, 'chair');

const northboundScenario = {
  id: 'audition-cold-read', title: 'Cold Read: Northbound', category: 'Audition practice', mode: 'audition', duration: '8–12 min',
  logline: 'Two siblings meet at a bus stop with one ticket and two different plans for who should leave.',
  description: 'A contemporary two-hander with humor, pressure, buried care, and a choice that can be played several ways.',
  characters: [
    { id: 'rowan', name: 'Rowan', role: 'Older sibling', color: '#72e0a6', initials: 'RO', overview: 'Composed on the surface, running out of options underneath.', publicInfo: 'Rowan bought the last northbound ticket.', secret: 'You bought it for Casey, not yourself.', objective: 'Get Casey onto the bus without admitting how worried you are.' },
    { id: 'casey', name: 'Casey', role: 'Younger sibling', color: '#f6df6b', initials: 'CA', overview: 'Restless, funny, and sharper than Rowan expects.', publicInfo: 'Casey says they have decided to stay.', secret: 'You already packed and want Rowan to ask honestly.', objective: 'Make Rowan say what the ticket really means.' },
  ],
  scenes: [{ id: 'north-1', number: 1, title: 'Last Bus', location: 'A rain-soaked bus shelter', situation: 'Rowan and Casey have ten minutes before the last northbound bus leaves, one ticket between them, and several things neither wants to say.', tone: 'Guarded affection under a ticking clock' }],
  cues: [
    line('north-c1', 'north-1', 'rowan', 'The bus is early', 'Hear the approaching bus and check the time.', 'Check the time.', 'The last northbound bus is five minutes early. Of course tonight is the night public transit becomes efficient.'),
    line('north-c2', 'north-1', 'rowan', 'Open lightly', 'Keep the ticket hidden and make the opening sound teasing instead of worried.', 'Hide the ticket.', 'You always did like making an exit complicated.'),
    line('north-c3', 'north-1', 'casey', 'Deflect with humor', 'Answer lightly, then notice Rowan is hiding something.', 'Watch Rowan.', 'Complicated is just interesting with luggage. Why are you standing like you stole something?'),
    line('north-c7', 'north-1', 'rowan', 'Deny it badly', 'Move the ticket behind your leg.', 'Hide it worse.', 'I am standing normally. This is a very normal bus-stop posture.'),
    line('north-c8', 'north-1', 'casey', 'Call it out', 'Point at Rowan’s hidden hand.', 'Point.', 'Your normal posture has a ticket sticking out of it.'),
    line('north-c9', 'north-1', 'rowan', 'Reveal the ticket', 'Bring the ticket into view but keep hold of it.', 'Show the ticket.', 'Fine. Last seat north. One way. Leaves in nine minutes.'),
    line('north-c10', 'north-1', 'casey', 'Refuse the easy story', 'Do not reach for the ticket.', 'Stay still.', 'And you bought it for yourself because you suddenly love buses?'),
    line('north-c11', 'north-1', 'rowan', 'Push Casey away', 'Try to sound practical instead of scared.', 'Stay practical.', 'I bought it because one of us needs to stop pretending this town has a future waiting around the corner.'),
    line('north-c12', 'north-1', 'casey', 'Make it personal', 'Step closer. Stop joking.', 'Step closer.', 'One of us? Say my name if you mean me.'),
    line('north-c13', 'north-1', 'rowan', 'Say it', 'Meet Casey’s eyes.', 'Meet their eyes.', 'Casey. I mean you.'),
    line('north-c14', 'north-1', 'casey', 'The packed bag', 'Lift your travel bag slightly.', 'Lift the bag.', 'I packed three hours ago. I was going to go. Then you started deciding for me again.'),
    line('north-c15', 'north-1', 'rowan', 'Rowan realizes', 'Look at the bag as if you are seeing it for the first time.', 'Look at the bag.', 'You were already leaving?'),
    line('north-c16', 'north-1', 'casey', 'Ask for honesty', 'Let the anger drop enough to show what you actually wanted.', 'Soften.', 'I wanted you to ask me to go. Not arrange me like luggage and call it helping.'),
    { id: 'north-c4', sceneId: 'north-1', type: 'adjustment', visibility: 'selected', characterIds: ['rowan', 'casey'], label: 'Audition adjustment', text: 'Play the next exchange as if neither person is allowed to say goodbye, even though both know that is what the scene is about.', requireAck: true },
    line('north-c17', 'north-1', 'rowan', 'Rowan admits fear', 'Stop defending yourself.', 'Drop the defense.', 'I am scared that if you stay because of me, you will wake up one day and hate me for it.'),
    line('north-c18', 'north-1', 'casey', 'Casey answers', 'Take that in before responding.', 'Take it in.', 'And I am scared that if I leave because you pushed me, I will make you the villain so I do not have to admit I chose it.'),
    line('north-c5', 'north-1', 'rowan', 'Give away the ticket', 'Place the ticket in Casey’s hand without closing their fingers around it.', 'Offer the ticket.', 'It has your name on it. But it is still your choice.'),
    line('north-c19', 'north-1', 'casey', 'Hear the bus', 'Look toward the arriving bus, then back at Rowan.', 'Look at the bus.', 'That is annoyingly mature. I had a whole speech prepared for when you stayed bossy.'),
    line('north-c20', 'north-1', 'rowan', 'One last joke', 'Let yourself smile.', 'Smile.', 'Text it to me from the bus. I will grade it.'),
    { id: 'north-c6', sceneId: 'north-1', type: 'ending', visibility: 'character', characterId: 'casey', label: 'Choose the ending', text: 'Choose whether to board, stay, or hand the ticket to someone else. Make Rowan understand your decision before you explain it.', requireAck: true },
  ],
  script: [],
};

const switchboardScenario = buildApprovedStory({
  "id": "improv-switchboard",
  "title": "Switchboard 9",
  "category": "Improv",
  "mode": "improv",
  "duration": "12–18 min",
  "logline": "Every impossible call changes the operator’s problem, until two callers discover their emergencies are connected.",
  "description": "A structured improv with strong story turns, private complications, and lots of room for actors to invent.",
  "characters": [
    {
      "id": "operator",
      "name": "Operator Nine",
      "role": "Switchboard operator",
      "color": "#55d7ff",
      "initials": "09",
      "overview": "The only calm person in a very strange call center.",
      "publicInfo": "You connect callers to impossible departments.",
      "secret": "This is your final shift and you want one perfect call.",
      "objective": "Solve every caller’s need without disconnecting."
    },
    {
      "id": "caller-a",
      "name": "Caller A",
      "role": "First caller",
      "color": "#ff7fa6",
      "initials": "A",
      "overview": "A caller with a problem that keeps changing.",
      "publicInfo": "You need to reach the Department of Lost Things.",
      "secret": "The lost thing is an emotion, not an object.",
      "objective": "Make the operator guess what you lost."
    },
    {
      "id": "caller-b",
      "name": "Caller B",
      "role": "Second caller",
      "color": "#ffb454",
      "initials": "B",
      "overview": "A suspiciously confident mystery caller.",
      "publicInfo": "You claim to be calling from tomorrow.",
      "secret": "You are actually sitting in the same room as Caller A.",
      "objective": "Warn the operator about something harmless as if it is world-ending."
    }
  ],
  "scenes": [
    {
      "id": "switch-1",
      "number": 1,
      "title": "Lines Open",
      "location": "An impossible switchboard",
      "situation": "Operator Nine is trying to finish one perfect final shift when a caller reports losing something that cannot be placed in a box.",
      "tone": "Fast, specific, playful"
    },
    {
      "id": "switch-2",
      "number": 2,
      "title": "Crossed Wires",
      "location": "Several impossible locations at once",
      "situation": "A second caller claims to be phoning from tomorrow, and the switchboard begins connecting details that should not connect.",
      "priorEvent": "Caller A described a missing emotion while Operator Nine treated every impossible claim as normal.",
      "tone": "Escalate, connect, then land the joke"
    }
  ]
}, 'switch');

export const museumScenario = {
  id: 'museum-after-hours', title: 'After Hours at Bellwether', category: 'Mystery', mode: 'mystery', duration: '15–20 min',
  logline: 'A portrait changes overnight, and each witness holds a clue that makes the impossible explanation harder to dismiss.',
  description: 'A clue-driven museum mystery with secrets, suspicion, family history, and a reveal that changes what the painting means.',
  characters: [
    { id: 'curator', name: 'Nia Hart', role: 'Museum curator', color: '#9b8cff', initials: 'NH', overview: 'Measured, protective, and deeply knowledgeable.', publicInfo: 'Nia catalogued the Bellwether portrait.', secret: 'You found a second signature beneath the frame yesterday and kept it out of the overnight report because you feared a false scandal.', objective: 'Discover what changed without hiding evidence to protect the museum.' },
    { id: 'guard', name: 'Owen Pike', role: 'Night guard', color: '#72e0a6', initials: 'OP', overview: 'Practical, observant, and difficult to rattle.', publicInfo: 'Owen was alone in the west gallery.', secret: 'At 2:13 a.m. you heard the person in the painting laugh.', objective: 'Prove you did not sleep through the change.' },
    { id: 'artist', name: 'Sana Bell', role: 'Restoration artist', color: '#ffb454', initials: 'SB', overview: 'Bold, intuitive, and personally invested.', publicInfo: 'Sana inspected the painting last week.', secret: 'The hidden second signature belongs to your grandmother.', objective: 'Learn whether the painting is revealing family history.' },
  ],
  scenes: [
    { id: 'museum-1', number: 1, title: 'The Changed Portrait', location: 'West gallery', situation: 'A nineteenth-century portrait that showed three people at closing now shows a fourth figure standing in the background.', tone: 'Quiet suspicion and precise observation' },
    { id: 'museum-2', number: 2, title: 'Under the Frame', location: 'Conservation table', situation: 'The group has moved the portrait for examination after finding clues that the change may be older than the museum itself.', priorEvent: 'Owen admitted hearing laughter, Nia revealed a second signature, and Sana recognized the name as her grandmother’s.', tone: 'Evidence, family history, and revelation' },
  ],
  cues: [
    line('museum-c1', 'museum-1', 'curator', 'A new figure appears', 'Bring the others to the portrait and point to the fourth figure before explaining it.', 'Point to the figure.', 'There were three people in this portrait yesterday. This morning there are four.'),
    line('museum-c28', 'museum-1', 'guard', 'Closing photo', 'Show the others the final security still from closing time.', 'Show the photo.', 'Six o’clock closing check. Three figures in the portrait. Time stamp, west gallery, no question.'),
    line('museum-c29', 'museum-1', 'curator', 'Frame untouched', 'Inspect the frame edges and the dust on the wall.', 'Check the frame.', 'The dust line is unbroken. Nobody took the painting down after closing.'),

    line('museum-c7', 'museum-1', 'artist', 'Sana checks the surface', 'Move close without touching the painting. Study the new figure’s edges.', 'Study the paint.', 'No wet shine. No raised edge. If somebody painted this last night, they somehow made fresh paint look a century old.'),
    line('museum-c8', 'museum-1', 'guard', 'Owen offers the log', 'Hold up the night log before anyone can blame you.', 'Raise the log.', 'The gallery stayed locked from eleven until six. I checked this room every hour.'),
    line('museum-c2', 'museum-1', 'guard', 'Remember the laugh', 'Look toward the far wall where the sound came from. Admit only part of what happened.', 'Look across the room.', 'At two thirteen, I heard someone laugh in here. I was awake, and I was alone.'),
    line('museum-c30', 'museum-1', 'guard', 'Camera glitch', 'Point to the camera above the gallery door.', 'Point to the camera.', 'There is one more thing. The camera went to static for eleven seconds at exactly two thirteen.'),
    line('museum-c31', 'museum-1', 'curator', 'Match the time', 'Look back at Owen’s log.', 'Check the log.', 'The exact minute you heard the laugh. That is the first detail tonight I like even less than the painting.'),

    line('museum-c9', 'museum-1', 'curator', 'Question the sound', 'Turn sharply to Owen.', 'Face Owen.', 'You wrote “unidentified sound” in the log. You left out the part where it sounded like a person?'),
    line('museum-c10', 'museum-1', 'guard', 'Tell the stranger part', 'Stop protecting your credibility.', 'Tell the truth.', 'Because it came from this side of the room. From the wall where the portrait hangs.'),
    line('museum-c11', 'museum-1', 'artist', 'Notice the fourth figure', 'Lean closer to the fourth figure’s face.', 'Lean closer.', 'The new woman is laughing. Look at her mouth. The other three people are not.'),
    line('museum-c3', 'museum-1', 'curator', 'Reveal the second signature', 'Turn the frame and show the mark you found yesterday.', 'Turn the frame.', 'There is something else. I found a second signature under the frame yesterday and did not know what it meant.'),
    line('museum-c32', 'museum-1', 'artist', 'Question Nia', 'Keep your eyes on the hidden signature.', 'Face Nia.', 'You found this yesterday. Why did you keep it quiet?'),
    line('museum-c33', 'museum-1', 'curator', 'Nia explains', 'Answer without defensiveness.', 'Answer plainly.', 'Because if that signature meant fraud, I did not want Bellwether dragged through a scandal before I knew the truth. I told myself waiting was careful.'),

    line('museum-c12', 'museum-1', 'artist', 'Recognize the handwriting', 'Read the signature twice. Let recognition hit slowly.', 'Read the name.', 'No. That cannot be here.'),
    line('museum-c13', 'museum-1', 'guard', 'Ask the obvious', 'Look between Sana and the signature.', 'Look at Sana.', 'You know that name?'),
    line('museum-c14', 'museum-1', 'artist', 'Sana delays', 'Take one breath before answering.', 'Take a breath.', 'I know the handwriting. I need better light before I say why.'),
    line('museum-c15', 'museum-1', 'curator', 'Move to conservation', 'Protect the portrait and end the guessing in the gallery.', 'Take command.', 'We move it to conservation. Nobody calls the press, nobody posts a photo, and nobody says “haunted” until we have evidence.'),

    line('museum-c4', 'museum-2', 'artist', 'Recognize the name', 'Set the frame under the light and finally say the connection aloud.', 'Say the name.', 'That signature belongs to my grandmother, Elena Bell.'),
    line('museum-c16', 'museum-2', 'curator', 'The dates do not fit', 'Check the catalog record.', 'Check the record.', 'Your grandmother was born in 1948. This portrait was painted in 1891.'),
    line('museum-c17', 'museum-2', 'artist', 'Family story', 'Touch the table, not the painting. Keep yourself grounded.', 'Steady yourself.', 'She restored this painting in 1976. She told my mother she found “someone hiding underneath,” but nobody knew what she meant.'),
    line('museum-c34', 'museum-2', 'artist', 'Marks on the backing', 'Angle the frame backing toward the light and notice faded pencil marks.', 'Tilt the frame.', 'There is writing here. Very faint. My grandmother’s initials, then: “Figure beneath blue veil. Ordered covered. 1976.”'),
    line('museum-c35', 'museum-2', 'curator', 'Ordered by whom', 'Move beside Sana to read the marks yourself.', 'Read beside her.', '“Ordered covered” means somebody knew the fourth figure existed and deliberately hid her again. Who gave the order?'),
    line('museum-c36', 'museum-2', 'artist', 'One more initial', 'Trace the last faded initials without touching the surface.', 'Find the initials.', 'W.B. Walter Bellwether. The museum’s founder.'),
    line('museum-c37', 'museum-2', 'guard', 'The mystery changes', 'Look at the museum around you, then back at the portrait.', 'Look around.', 'So the museum founder knew there was a fourth person in the painting and paid to make her disappear again?'),

    line('museum-c18', 'museum-2', 'guard', 'Hidden layer theory', 'Point to the fourth figure.', 'Point to her.', 'What if she did not add the woman? What if she uncovered her?'),
    line('museum-c19', 'museum-2', 'curator', 'Use the report', 'Open the pigment report and compare both paint samples.', 'Open the report.', 'We sampled the original blue and the blue around the new figure. Same mineral mix. Same aging pattern.'),
    line('museum-c5', 'museum-2', 'curator', 'The paint is old', 'Read the conclusion twice before sharing it.', 'Read the result.', 'The fourth figure was painted with material from the original century. She was not added last night.'),
    line('museum-c20', 'museum-2', 'artist', 'A hidden restoration', 'Look at the signature again and connect the family story.', 'Connect the clues.', 'My grandmother must have uncovered her, then covered her again. The signature was her way of marking what she found.'),
    line('museum-c21', 'museum-2', 'guard', 'But why now', 'Shake your head. One mystery remains.', 'Shake your head.', 'That explains 1976. It does not explain why the woman was hidden yesterday and visible this morning.'),
    line('museum-c38', 'museum-2', 'curator', 'Accession condition', 'Check the old catalog record beside the pigment report.', 'Check the record.', 'The original donor required the portrait to remain “as received.” Bellwether may have thought uncovering her would violate the agreement.'),
    line('museum-c39', 'museum-2', 'artist', 'Sana rejects the excuse', 'Look at the fourth figure, not the paperwork.', 'Look at her.', 'Maybe. But you almost repeated it. You found evidence and decided the rest of us could wait for the truth.'),

    line('museum-c22', 'museum-2', 'curator', 'Stop hiding evidence', 'Inspect the back of the frame and choose to share every clue from here on.', 'Check the backing.', 'You are right. No more missing clues. This backing board shifted, and there is a pressure strip along the edge. We check it together.'),
    line('museum-c23', 'museum-2', 'artist', 'Humidity changed it', 'Look at the conservation meter.', 'Check the meter.', 'The gallery humidity dropped hard overnight. If the top glaze contracted, a hidden layer could have turned transparent.'),
    line('museum-c24', 'museum-2', 'guard', 'The laugh remains', 'Accept the physical answer, then point out the detail it does not solve.', 'Raise one finger.', 'Good. Science explains the woman. Science still owes me an explanation for the laugh at two thirteen.'),
    line('museum-c40', 'museum-2', 'guard', 'Test the sound', 'Stand where you heard the laugh and listen while Sana taps the frame hardware.', 'Listen from there.', 'Do that again. From this spot, with the vent behind me, it is close. Uncomfortably close.'),
    line('museum-c41', 'museum-2', 'artist', 'Recreate the laugh', 'Tap the wire once more, then stop.', 'Tap it again.', 'Wood contracts, wire vibrates, the vent carries it across the room. That could explain the sound without explaining away how creepy it was.'),

    line('museum-c25', 'museum-2', 'artist', 'A possible sound', 'Look at the old frame hardware and tap it gently.', 'Tap the frame.', 'This tension wire can vibrate when the wood shifts. Through the gallery vent... it could sound almost human.'),
    line('museum-c26', 'museum-2', 'curator', 'Not less wonderful', 'Look at the fourth figure with new respect.', 'Look at the portrait.', 'Maybe nothing supernatural happened. But we are not hiding her again. Tomorrow we show the evidence, including my signature note, and say what we still do not know.'),
    line('museum-c27', 'museum-2', 'artist', 'Name the real mystery', 'Step beside Nia and look at the fourth figure.', 'Step beside Nia.', 'The mystery is not how she appeared. It is why someone painted her out in the first place.'),
    { id: 'museum-c6', sceneId: 'museum-2', type: 'ending', visibility: 'selected', characterIds: ['curator', 'guard', 'artist'], label: 'Choose the public story', text: 'Together decide what goes on the public label tomorrow, what remains uncertain, and which question you investigate next. Each person must defend one choice using evidence from tonight.', requireAck: true },
  ],
  script: [],
};

export const retainedScenarioLibrary = [missingRavenScenario,lastChairScenario,northboundScenario,switchboardScenario];
export const scenarioLibrary = [
  buildExpandedEchoStation(demoScenario),
  buildExpandedAuthoredStory(missingRavenScenario,'raven'),
  buildExpandedAuthoredStory(lastChairScenario,'chair'),
  buildExpandedAuthoredStory(northboundScenario,'northbound'),
  buildExpandedAuthoredStory(switchboardScenario,'switch'),
  buildExpandedBellwether(museumScenario),
  buildExpandedMapRoom(mapRoomMurderScenario),
];
